import InputAdornment from "@mui/material/InputAdornment";
import Skeleton from "@mui/material/Skeleton";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { PathBackButton } from "src/components/Default/LekUI/BackButton";
import {
	OutlinedPrimaryButton,
	PrimaryButton,
} from "src/components/Default/LekUI/Button";
import type { SortOption } from "src/components/Default/LekUI/SortButton";
import { SortButton } from "src/components/Default/LekUI/SortButton";
import { TextFieldGrey } from "src/components/Default/LekUI/TextField";
import Box from "src/components/Default/Main/Box";
import { FolderEmptyListView } from "src/components/Folders/FolderEmptyListView";
import { FolderListItem } from "src/components/Folders/FolderListItem";
import { Header } from "src/components/Main/ExamsBox/ExamsBox";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import { LayoutWithSearch } from "src/containers/Layouts/LayoutWithSearch";
import isMobile from "src/helpers/isMobile";
import { useFoldersSearchInfinite } from "src/hooks/useFolders";
import FolderIcon from "src/images/Icons/newIcons/folder.svg";
import SearchIcon from "src/images/Icons/newIcons/search.svg";

const DEFAULT_ORDER_BY = "updatedAt";
const DEFAULT_ORDER = "DESC";

type FolderOrderBy = "updatedAt" | "createdAt" | "name";
type FolderOrder = "ASC" | "DESC";

const SORT_OPTIONS: SortOption<FolderOrderBy, FolderOrder>[] = [
	{
		orderBy: "updatedAt",
		order: "DESC",
		labelKey: "foldersList.sort.updatedAt",
		labelFallback: "Data aktualizacji",
	},
	{
		orderBy: "updatedAt",
		order: "ASC",
		labelKey: "foldersList.sort.updatedAt",
		labelFallback: "Data aktualizacji",
	},
	{
		orderBy: "createdAt",
		order: "DESC",
		labelKey: "foldersList.sort.createdAt",
		labelFallback: "Data utworzenia",
	},
	{
		orderBy: "createdAt",
		order: "ASC",
		labelKey: "foldersList.sort.createdAt",
		labelFallback: "Data utworzenia",
	},
	{
		orderBy: "name",
		order: "ASC",
		labelKey: "foldersList.sort.alphabetical",
		labelFallback: "Alfabetycznie",
	},
	{
		orderBy: "name",
		order: "DESC",
		labelKey: "foldersList.sort.alphabetical",
		labelFallback: "Alfabetycznie",
	},
];

export const FoldersList = () => {
	const { t } = useTranslation();
	const { openModal } = useModals();
	const history = useHistory();
	const location = useLocation();
	const queryClient = useQueryClient();
	const [searchValue, setSearchValue] = useState("");

	useEffect(() => {
		return () => {
			queryClient.removeQueries({
				queryKey: ["folders-infinite"],
			});
		};
	}, [queryClient]);

	const searchParams = new URLSearchParams(location.search);
	const orderBy = searchParams.get("orderBy") || DEFAULT_ORDER_BY;
	const order = searchParams.get("order") || DEFAULT_ORDER;

	const foundIndex = SORT_OPTIONS.findIndex(
		(opt) => opt.orderBy === orderBy && opt.order === order,
	);
	const selectedSortIndex = foundIndex === -1 ? 0 : foundIndex;

	const setSort = (newOrderBy: string, newOrder: string) => {
		const params = new URLSearchParams(location.search);
		if (newOrderBy === DEFAULT_ORDER_BY && newOrder === DEFAULT_ORDER) {
			params.delete("orderBy");
			params.delete("order");
		} else {
			params.set("orderBy", newOrderBy);
			params.set("order", newOrder);
		}
		const qs = params.toString();
		history.replace(`${location.pathname}${qs ? `?${qs}` : ""}`);
	};

	const {
		data,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isLoading,
		isFetching,
	} = useFoldersSearchInfinite({
		take: 10,
		orderBy: orderBy as "createdAt" | "updatedAt" | "name",
		order: order as "ASC" | "DESC",
		...(searchValue ? { search: searchValue } : {}),
	});

	const flatFolders = useMemo(() => {
		if (!data?.pages) return [];
		return data.pages.flatMap((page) => page.data || []);
	}, [data?.pages]);

	const totalCount = data?.pages?.[0]?.totalCount || 0;

	const renderSearch = () => {
		return (
			<div className="flex items-center gap-16 w-100">
				<TextFieldGrey
					variant="filled"
					placeholder={t("foldersList.search", "Szukaj folderu")}
					value={searchValue}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						setSearchValue(e.target.value)
					}
					className="w-100 search--text-field max-w-350"
					autoFocus={!isMobile()}
					loading={isFetching && !isLoading}
					slotProps={{
						htmlInput: { "data-test-id": "folders-list-search-input" },
						input: {
							disableUnderline: true,
							startAdornment: (
								<InputAdornment position="start" className="m-0 mr-8">
									<SearchIcon className="icon-normal icon-black-80" />
								</InputAdornment>
							),
						},
					}}
				/>
				<SortButton
					options={SORT_OPTIONS}
					selectedIndex={selectedSortIndex}
					onSelect={(index) => {
						const opt = SORT_OPTIONS[index];
						setSort(opt.orderBy, opt.order);
					}}
					testId="folders-sort-button"
					containerName="folders-list"
				/>
			</div>
		);
	};

	return (
		<LayoutWithSearch>
			<div className="flex flex-col w-100 h-100 relative folders-list-container pb-32">
				<Box
					className="flex flex-col gap-16"
					headerClassName="gap-16 mb-0"
					Header={
						<Header
							title={t("foldersList.title", "Foldery")}
							icon={
								<>
									<PathBackButton />
									<FolderIcon className="icon-super-big" />
								</>
							}
							titleClassName="justify-between w-100"
							additionalActions={
								<PrimaryButton
									data-test-id="create-folder-button"
									onClick={() => openModal(ModalsNames.CreateFolderModal)}
								>
									+ {t("foldersList.newFolder", "Nowy folder")}
								</PrimaryButton>
							}
						/>
					}
					additionalTabsActions={renderSearch}
					tabsClassName="justify-start"
					tabs={[]}
				>
					<div className="flex flex-col w-100 gap-16">
						{isLoading ? (
							<div className="folders-list-grid">
								{Array.from({ length: 5 }).map((_, i) => (
									<Skeleton
										key={i}
										variant="rounded"
										height={58}
										className="border-radius-16"
									/>
								))}
							</div>
						) : flatFolders.length === 0 ? (
							<FolderEmptyListView
								title={t(
									"foldersList.empty.title",
									"Nie masz jeszcze żadnego folderu",
								)}
								text={t(
									"foldersList.empty.description",
									"Utwórz swój pierwszy folder, aby organizować materiały",
								)}
								buttonText={t(
									"foldersList.empty.button",
									"Dodaj pierwszy folder",
								)}
								onClick={() => openModal(ModalsNames.CreateFolderModal)}
							/>
						) : (
							<div className="folders-list-grid">
								{flatFolders.map((folder) => (
									<FolderListItem key={folder.id} folder={folder} />
								))}
							</div>
						)}

						{hasNextPage && (
							<div className="flex justify-center items-center gap-16">
								<OutlinedPrimaryButton
									data-test-id="folders-list-load-more-button"
									onClick={() => fetchNextPage()}
									disabled={isFetchingNextPage}
								>
									{isFetchingNextPage
										? t("folder.loading", "Ładowanie...")
										: t("folder.loadMore", "Załaduj więcej")}
								</OutlinedPrimaryButton>
								<div className="flex flex-col justify-center size-14 text-black-80">
									<span>{t("folder.showing", "Wyświetlane:")}</span>
									<span>
										{t(
											"folder.showingCountValues",
											"{{loaded}} z {{total}} elementów",
											{
												loaded: flatFolders.length,
												total: totalCount,
											},
										)}
									</span>
								</div>
							</div>
						)}
					</div>
				</Box>
			</div>
		</LayoutWithSearch>
	);
};
